import { Reveal, Chapter } from "./Reveal";
import { ChevronRight, ChevronDown, Crosshair } from "lucide-react";

const STEPS = [
  {
    n: 1,
    title: "Biznesa mērķis",
    label: "Analīze",
    desc: "Kāds biznesa rezultāts kreatīvam ir jāsasniedz?",
    sub: "Piemēri",
    bullets: ["Zīmola atpazīstamība", "Potenciālo klientu piesaiste", "Pārdošana internetā", "Produkta ieviešana", "Remārketings", "Pasākuma reklāma", "Personāla piesaiste"],
    audit: true,
  },
  {
    n: 2,
    title: "Auditorijas izpēte",
    label: "Analīze",
    desc: "Ko mēs vēlamies uzrunāt un kas motivē šo auditoriju?",
    sub: "Mēs nosakām",
    bullets: ["Mērķauditorija", "Sāpju punkti", "Motivācija", "Pirkuma trigeri", "Iebildumi", "Emocionālie dzinēji"],
    audit: true,
  },
  {
    n: 3,
    title: "Kreatīvā stratēģija",
    label: "Domāšana",
    desc: "Izstrādāt pilnu kreatīva koncepciju.",
    sub: "Būvēts pēc 6 stratēģisko performance elementu sistēmas",
    bullets: ["Galvenais vēstījums", "Kreatīvā pieeja (Creative Angle)", "Uzmanības piesaiste (Hook, stop scrolling)", "Vizuālais virziens", "Pamatojums un uzticības elementi", "Aicinājums uz rīcību"],
  },
  {
    n: 4,
    title: "Ražošana",
    label: "Darīšana",
    desc: "Izstrādāt reklāmas materiālus.",
    sub: "Būvēts pēc 8 kreatīvu asset elementu sistēmas",
    bullets: ["Virsraksts / 3 sec", "Apakšvirsraksts", "Vērtības piedāvājums", "Galvenais attēls / video", "Papildu vizuālie elementi", "Zīmola elementi", "Aicinājums uz rīcību"],
  },
  {
    n: 5,
    title: "Kvalitātes pārbaude",
    label: "Izvērtēšana",
    desc: "Izvērtēt kreatīvu pēc noteiktiem pārdošanas efektivitātes kritērijiem.",
    sub: "Katru kritēriju vērtējam no 1 līdz 5",
    bullets: ["Atbilstība mērķim", "Uzmanības piesaiste", "Vēstījuma skaidrība", "Kreatīva ietekme", "Vērtības piedāvājums", "Pamatojums un uzticības elementi", "Vizuālā hierarhija", "CTA efektivitāte", "Zīmola konsekvence"],
    audit: true,
    highlight: true,
  },
  {
    n: 6,
    title: "Testēšana un optimizācija",
    label: "Reklāma",
    desc: "Analīze, secinājumi, priekšlikumi.",
  },
];

const StepCard = ({ step }) => (
  <div
    data-testid={step.highlight ? "framework-step-highlight" : `framework-step-${step.n}`}
    className={`relative h-full rounded-sm bg-white border border-black/10 border-t-4 p-6 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${
      step.highlight ? "border-t-[#ED1C27] shadow-[0_20px_60px_-25px_rgba(237,28,39,0.35)]" : "border-t-[#ED1C27]"
    }`}
  >
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ED1C27] text-white font-display font-black text-sm">
        {step.n}
      </span>
      <h3 className="font-display font-bold tracking-tight text-lg leading-tight text-[#003E75]">{step.title}</h3>
    </div>

    <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.25em] text-[#ED1C27]">{step.label}</p>
    <div className="mt-2 h-px bg-black/10" />

    <p className="mt-4 text-sm text-[#4C4C4D] leading-relaxed">{step.desc}</p>

    {step.audit && (
      <span className="mt-4 inline-flex w-fit items-center gap-1.5 bg-[#ED1C27] text-white text-[9px] font-bold uppercase tracking-[0.14em] px-2.5 py-1.5 rounded-full">
        <Crosshair className="h-2.5 w-2.5" />
        Šeit notiek jūsu audits
      </span>
    )}

    {step.sub && (
      <p className="mt-5 text-sm font-bold text-[#003E75] leading-snug">{step.sub}</p>
    )}

    {step.bullets && (
      <>
        <div className="mt-4 h-0.5 w-8 bg-[#ED1C27]" />
        <ul className="mt-4 space-y-2.5">
          {step.bullets.map((b) => (
            <li key={b} className="flex gap-2.5 text-[13px] text-[#4C4C4D] leading-snug">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#ED1C27]" />
              {b}
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export default function Framework() {
  return (
    <section id="sistema" data-testid="framework-section" className="bg-[#F9F9F9] border-t border-black/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
        <Chapter number="03" label="Sistēma" />

        <Reveal>
          <h2 className="font-display font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#003E75] max-w-4xl" data-testid="framework-heading">
            Audita pamatā ir sistēma, ko paši izmantojam{" "}
            <span className="text-[#003E75]/30">katru dienu</span>
          </h2>
        </Reveal>

        <Reveal delay={0.08} className="mt-8 max-w-3xl text-[#4C4C4D] text-base leading-relaxed">
          <p>
            Mēs neesam izveidojuši atsevišķu teorētisku checklist tikai tāpēc, lai piedāvātu
            bezmaksas auditu. Audita pamatā ir mūsu pašu kreatīvu izstrādes metodoloģija:
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-10">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#ED1C27]">Mūsu metodoloģija</p>
          <p
            className="mt-3 font-display font-black tracking-tighter leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-[#003E75]"
            data-testid="framework-name"
          >
            iM <span className="text-[#ED1C27]">Result-Driven</span> Creative Production System
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div data-testid="framework-scheme" className="grid gap-10 md:grid-cols-2 lg:grid-cols-6 lg:gap-7">
            {STEPS.map((step, i) => (
              <div key={step.n} className="relative">
                <StepCard step={step} />
                {i < STEPS.length - 1 && (
                  <>
                    <ChevronRight className="hidden lg:block absolute top-10 -right-[22px] h-5 w-5 text-[#ED1C27] z-10" />
                    <ChevronDown className="lg:hidden mx-auto mt-4 h-5 w-5 text-[#ED1C27]" />
                  </>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-20 grid lg:grid-cols-2 gap-12 items-start max-w-5xl">
          <Reveal>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight font-bold text-[#003E75]" data-testid="quality-review-heading">
              Jūsu kreatīvus ievietosim mūsu sistēmas Kvalitātes pārbaudes posmā
            </h3>
            <p className="mt-4 text-[#4C4C4D] text-base leading-relaxed">
              Tas nozīmē, ka Jūsu banerus un video vērtēsim pēc tās pašas metodoloģijas, ko
              izmantojam, pārbaudot mūsu pašu klientiem izstrādātos kreatīvus pirms un pēc to palaišanas.
            </p>
          </Reveal>
          <div className="space-y-4">
            <Reveal delay={0.05}>
              <div className="rounded-sm border border-black/15 bg-white p-5" data-testid="audit-goal-not">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4C4C4D]/70 mb-2">Audita mērķis NAV</p>
                <p className="text-[#4C4C4D] text-sm">“Mēs šo banneri uztaisītu citādi.”</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-sm border border-[#ED1C27] bg-[#ED1C27]/5 p-5" data-testid="audit-goal-is">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ED1C27] mb-2">Audita mērķis IR</p>
                <p className="text-[#003E75] text-sm">
                  Identificēt, kur tieši kreatīvs zaudē uzmanību, interesi vai vēlmi rīkoties - un ko uzlabot.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
